import type { CellPosition, Column, GridApi } from "@ag-grid-community/core";
import { assertNever } from "@regrello/core-utils";
import { useCallback, useState } from "react";
import { useMount } from "react-use";

/** The minimum width of the editor in a cell. */
const MIN_EDITOR_WIDTH_PX = 120;

export namespace useDataGridV2EditableCell {
  export interface Args {
    /** The `GridApi` instance from `RegrelloDataGridV2`. */
    api: GridApi;

    /**
     * The event key that initiated edit mode in the current cell. `Enter` will result in slightly
     * different, "stickier" behavior, where arrow keys only navigate within the current cell.
     */
    eventKey: string | null;

    /**
     * If `true`, the `ArrowDown` key will not exit edit mode. Recommended if the arrow up/down key
     * is meaningful in a given edit experiences.
     *
     * @default false
     */
    suppressKeyArrowDown?: boolean;

    /**
     * If `true`, the `ArrowLeft` key will not exit edit mode. Recommended if the arrow up/down key
     * is meaningful in a given edit experiences.
     *
     * @default false
     */
    suppressKeyArrowLeft?: boolean;

    /**
     * If `true`, the `ArrowRight` key will not exit edit mode. Recommended if the arrow up/down key
     * is meaningful in a given edit experiences.
     *
     * @default false
     */
    suppressKeyArrowRight?: boolean;

    /**
     * If `true`, the `ArrowUp` key will not exit edit mode. Recommended if the arrow up/down key is
     * meaningful in a given edit experiences.
     *
     * @default false
     */
    suppressKeyArrowUp?: boolean;

    /**
     * If `true`, the `Alt+Enter` key will not exit edit mode. Recommended if the `Enter` key is
     * used to move the cursor within the input.
     *
     * @default false
     */
    suppressKeyAltEnter?: boolean;

    /**
     * If `true`, the `Cmd+Enter` key will not exit edit mode. Recommended if the `Enter` key is
     * used to move the cursor within the input.
     *
     * @default false
     */
    suppressKeyCmdEnter?: boolean;
  }

  export interface Return {
    /**
     * The current width in pixels of the editing cell. This can be used to size your input to fill
     * the width of the cell. This value will be updated when the column is resized.
     */
    columnWidth: number;

    /**
     * Keydown handler that should listen for key events in the editing cell's input during the
     * capture phase of event propagation, not the default bubble phase.
     *
     * This function moves the focus to the next or previous cell when the user presses `Enter` and
     * cleans up other selection state.
     */
    onKeyDownCapture: React.KeyboardEventHandler<HTMLInputElement>;
  }
}

/**
 * Hook that encapsulates some common logic for properly managing microinteractions in editable
 * cells in `RegrelloDataGridV2`.
 */
export function useDataGridV2EditableCell({
  api,
  eventKey,
  suppressKeyArrowDown,
  suppressKeyArrowLeft,
  suppressKeyArrowRight,
  suppressKeyArrowUp,
  suppressKeyAltEnter,
  suppressKeyCmdEnter,
}: useDataGridV2EditableCell.Args): useDataGridV2EditableCell.Return {
  const [columnWidth, setColumnWidthUnclamped] = useState(0);

  const setColumnWidth = useCallback((width: number) => {
    setColumnWidthUnclamped(Math.max(width, MIN_EDITOR_WIDTH_PX));
  }, []);

  useMount(() => {
    const cell = getEditingCellSafely(api);
    if (cell == null) {
      console.warn("No editing cell found");
      return;
    }

    // Match the initial column width.
    const width = cell.column.getActualWidth();
    setColumnWidth(width);

    // Listen for width changes.
    cell.column.addEventListener("widthChanged", (event) => {
      if (event.column != null) {
        setColumnWidth(event.column.getActualWidth());
      }
    });
  });

  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const cell = getEditingCellSafely(api);
      if (cell == null) {
        console.warn("No editing cell found");
        return;
      }

      switch (event.key) {
        case "Enter":
          if (suppressKeyAltEnter && event.altKey) {
            break;
          }
          if (suppressKeyCmdEnter && event.metaKey) {
            break;
          }
          saveEditAndMoveFocusedCell(api, cell, event.shiftKey ? "up" : "down");
          break;

        // Arrow keys should leave edit mode unless edit mode was entered via (1) double click or
        // (2) `Enter` key.
        case "ArrowUp":
          if (!suppressKeyArrowUp && eventKey != null && eventKey !== "Enter") {
            saveEditAndMoveFocusedCell(api, cell, "up");
          }
          break;
        case "ArrowDown":
          if (!suppressKeyArrowDown && eventKey != null && eventKey !== "Enter") {
            saveEditAndMoveFocusedCell(api, cell, "down");
          }
          break;
        case "ArrowLeft":
          if (!suppressKeyArrowLeft && eventKey != null && eventKey !== "Enter") {
            saveEditAndMoveFocusedCell(api, cell, "left");
          }
          break;
        case "ArrowRight":
          if (!suppressKeyArrowRight && eventKey != null && eventKey !== "Enter") {
            saveEditAndMoveFocusedCell(api, cell, "right");
          }
          break;
        default:
          break;
      }
    },
    [
      api,
      eventKey,
      suppressKeyArrowDown,
      suppressKeyArrowLeft,
      suppressKeyArrowRight,
      suppressKeyArrowUp,
      suppressKeyAltEnter,
      suppressKeyCmdEnter,
    ],
  );

  return {
    columnWidth,
    onKeyDownCapture: handleKeyDownCapture,
  };
}

function getEditingCellSafely(api: GridApi): CellPosition | null {
  const cells = api.getEditingCells();
  if (cells.length === 0) {
    return null;
  }
  return cells[0];
}

function saveEditAndMoveFocusedCell(api: GridApi, cell: CellPosition, direction: "up" | "down" | "left" | "right") {
  api.stopEditing();
  api.clearCellSelection();

  // Update both the focused cell and the visibly selected cell.
  const nextCellPosition = getNextCellPosition(api, cell, direction);
  api.setFocusedCell(nextCellPosition.row, nextCellPosition.columnId);
  api.addCellRange({
    columns: [nextCellPosition.columnId],
    rowStartIndex: nextCellPosition.row,
    rowEndIndex: nextCellPosition.row,
  });
}

function getNextCellPosition(
  api: GridApi,
  cell: CellPosition,
  direction: "up" | "down" | "left" | "right",
): { columnId: string; row: number } {
  const originalPosition = {
    columnId: cell.column.getId(),
    row: cell.rowIndex,
  };

  switch (direction) {
    case "up": {
      if (cell.rowIndex - 1 < 0) {
        return originalPosition;
      }
      return { ...originalPosition, row: cell.rowIndex - 1 };
    }

    case "down": {
      if (cell.rowIndex + 1 >= api.getDisplayedRowCount()) {
        return originalPosition;
      }
      return { ...originalPosition, row: cell.rowIndex + 1 };
    }

    case "left": {
      const columnIndex = getColumnIndex(api, cell.column.getId());
      const prevColumnIndex = columnIndex - 1;
      if (prevColumnIndex < 0) {
        return originalPosition;
      }
      const prevColumnId = getColumnAtIndex(api, prevColumnIndex)?.getId();
      if (prevColumnId == null) {
        return originalPosition;
      }
      return { ...originalPosition, columnId: prevColumnId };
    }

    case "right": {
      const columnIndex = getColumnIndex(api, cell.column.getId());
      const nextColumnIndex = columnIndex + 1;
      if (nextColumnIndex >= getColumnCount(api)) {
        return originalPosition;
      }
      const nextColumnId = getColumnAtIndex(api, nextColumnIndex)?.getId();
      if (nextColumnId == null) {
        return originalPosition;
      }
      return { ...originalPosition, columnId: nextColumnId };
    }

    default:
      assertNever(direction);
  }
}

function getColumnCount(api: GridApi): number {
  return api.getColumns()?.length ?? 0;
}

function getColumnIndex(api: GridApi, columnId: string): number {
  return api.getColumns()?.findIndex((col) => col.getId() === columnId) ?? -1;
}

function getColumnAtIndex<T>(api: GridApi, index: number): Column<T> | null {
  return api.getColumns()?.[index] ?? null;
}
